export interface SEOData {
  title: string;
  description: string;
  keywords?: string;
  canonical?: string;
  hreflang?: {
    ja?: string;
    en?: string;
    default?: string;
  };
  schema?: any;
}

export const updateSEO = (data: SEOData) => {
  // Update document title
  document.title = data.title;
  
  // Update meta description
  const metaDescription = document.querySelector('meta[name="description"]');
  if (metaDescription) {
    metaDescription.setAttribute('content', data.description);
  } else {
    const meta = document.createElement('meta');
    meta.name = 'description';
    meta.content = data.description;
    document.head.appendChild(meta);
  }

  // Update meta keywords if provided
  if (data.keywords) {
    let metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', data.keywords);
    } else {
      const meta = document.createElement('meta');
      meta.name = 'keywords';
      meta.content = data.keywords;
      document.head.appendChild(meta);
    }
  }

  // Update canonical link if provided
  if (data.canonical) {
    const baseUrl = import.meta.env.VITE_SITE_URL || 'https://butler-association.jp';
    const abs = (u: string) => /^https?:\/\//.test(u) ? u : baseUrl + u;
    // Normalize trailing slash so runtime canonical matches the static prerender output
    // (root '/' preserved; any deeper path loses its trailing slash). Prevents Lighthouse
    // "Multiple conflicting URLs" between the slash and non-slash variants.
    const normUrl = (u: string) => u.replace(/^(https?:\/\/[^/]+\/[^?#]*?)\/+(?=$|[?#])/, '$1');
    const href = normUrl(abs(data.canonical));
    const canonicals = document.querySelectorAll('link[rel="canonical"]');
    if (canonicals.length) {
      // Force every canonical (static prerender + any React-hoisted inline) to the same
      // normalized slash-less href. Not removing nodes avoids React 19 removeChild races;
      // identical hrefs eliminate the "conflicting URLs" audit failure.
      canonicals.forEach((el) => el.setAttribute('href', href));
    } else {
      const link = document.createElement('link');
      link.rel = 'canonical';
      link.href = href;
      document.head.appendChild(link);
    }
  }

  // Update Open Graph / Twitter Card tags (keep in sync with title/description/canonical・absで絶対URL防御)
  {
    const baseUrl = import.meta.env.VITE_SITE_URL || 'https://butler-association.jp';
    const abs = (u: string) => /^https?:\/\//.test(u) ? u : baseUrl + u;
    const normUrl = (u: string) => u.replace(/^(https?:\/\/[^/]+\/[^?#]*?)\/+(?=$|[?#])/, '$1');
    const isEn = !!data.canonical && /^(?:https?:\/\/[^/]+)?\/en(?:\/|$)/.test(data.canonical);
    const ogPairs: Array<[string, string]> = [
      ['og:title', data.title],
      ['og:description', data.description],
      ['og:locale', isEn ? 'en_US' : 'ja_JP'],
    ];
    if (data.canonical) ogPairs.push(['og:url', normUrl(abs(data.canonical))]);
    ogPairs.forEach(([prop, content]) => {
      let el = document.querySelector(`meta[property="${prop}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('property', prop);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    });
    const twPairs: Array<[string, string]> = [
      ['twitter:title', data.title],
      ['twitter:description', data.description],
    ];
    twPairs.forEach(([name, content]) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('name', name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    });
  }

  // Add hreflang tags if provided
  if (data.hreflang) {
    // Remove existing hreflang tags
    const existingHreflang = document.querySelectorAll('link[rel="alternate"][hreflang]');
    existingHreflang.forEach(link => link.remove());

    const baseUrl = import.meta.env.VITE_SITE_URL || 'https://butler-association.jp';
    const abs = (u: string) => /^https?:\/\//.test(u) ? u : baseUrl + u;

    // Add Japanese version
    if (data.hreflang.ja) {
      const linkJa = document.createElement('link');
      linkJa.rel = 'alternate';
      linkJa.hreflang = 'ja';
      linkJa.href = abs(data.hreflang.ja);
      document.head.appendChild(linkJa);
    }

    // Add English version
    if (data.hreflang.en) {
      const linkEn = document.createElement('link');
      linkEn.rel = 'alternate';
      linkEn.hreflang = 'en';
      linkEn.href = abs(data.hreflang.en);
      document.head.appendChild(linkEn);
    }

    // Add x-default
    if (data.hreflang.default) {
      const linkDefault = document.createElement('link');
      linkDefault.rel = 'alternate';
      linkDefault.hreflang = 'x-default';
      linkDefault.href = abs(data.hreflang.default);
      document.head.appendChild(linkDefault);
    }
  }

  // Add Schema.org JSON-LD if provided
  if (data.schema) {
    // Remove existing schema
    const existingSchema = document.querySelector('script[type="application/ld+json"]');
    if (existingSchema) {
      existingSchema.remove();
    }
    
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(data.schema);
    document.head.appendChild(script);
  }
};

export const seoData: Record<string, SEOData> = {
  // 日本語ページ - トップページ
  '/': {
    title: '一般社団法人 全日本執事協会 | オフィシャルサイト',
    description: '一般社団法人 全日本執事協会は、執事文化の普及と専門知識の向上を目指す日本唯一の執事専門団体です。初級から上級までの執事資格認定制度、食事サービスやワイン、ビジネスマナーなど6分野の専門資格、実践的なセミナー・講座の開催、国際交流プログラムを通じて、世界に通用するプロフェッショナルな執事を育成しています。執事を目指す方、ホスピタリティスキルを高めたい方、法人研修をお考えの企業様はぜひご相談ください。',
    keywords: '全日本執事協会,執事,資格,研修,サービス,ホスピタリティ',
    canonical: '/',
    hreflang: {
      ja: '/',
      en: '/en',
      default: '/'
    }
  },

  // 日本語ページ - 協会について
  '/about': {
    title: '協会について｜理念・活動内容 | 一般社団法人 全日本執事協会',
    description: '一般社団法人 全日本執事協会の理念と活動内容をご紹介します。執事文化の普及と質の高いサービスを提供できる人材育成を目的に、資格認定、教育研修、国際交流などの事業を展開しています。',
    keywords: '執事協会,協会概要,理念,活動内容,人材育成,資格認定',
    canonical: '/about',
    hreflang: {
      ja: '/about',
      en: '/en/about',
      default: '/about'
    }
  },
  '/about/overview': {
    title: '協会概要｜設立目的と事業内容 | 一般社団法人 全日本執事協会',
    description: '全日本執事協会の設立背景と組織概要をご紹介。執事業界の統括団体として、資格認定制度の確立、専門教育の提供、国際的なネットワーク構築を行っています。協会の全体像をご覧いただけます。',
    keywords: '執事協会,協会概要,設立目的,事業内容,組織,資格認定',
    canonical: '/about/overview',
    hreflang: {
      ja: '/about/overview',
      en: '/en/about/overview',
      default: '/about/overview'
    }
  },
  '/about/mission': {
    title: '設立理念と使命｜執事文化の普及と発展 | 一般社団法人 全日本執事協会',
    description: '全日本執事協会が掲げる理念と使命について詳しくご紹介。洗練されたサービス文化の創造、執事としてのプロフェッショナリズムの追求、日本独自の執事文化の確立を3つの柱として活動しています。',
    keywords: '執事協会,設立理念,使命,執事文化,プロフェッショナリズム,サービス文化',
    canonical: '/about/mission',
    hreflang: {
      ja: '/about/mission',
      en: '/en/about/mission',
      default: '/about/mission'
    }
  },
  '/about/history': {
    title: '沿革・歴史｜協会の歩み | 一般社団法人 全日本執事協会',
    description: '全日本執事協会の設立から現在に至るまでの歴史を年表形式でご紹介。協会設立の経緯、資格制度の確立過程、国際的な活動の広がりなど、これまでの足跡を詳しくご覧いただけます。',
    keywords: '執事協会,沿革,歴史,年表,設立,発展',
    canonical: '/about/history',
    hreflang: {
      ja: '/about/history',
      en: '/en/about/history',
      default: '/about/history'
    }
  },
  '/about/organization': {
    title: '組織体制・役員紹介 | 一般社団法人 全日本執事協会',
    description: '全日本執事協会の組織構造と運営体制について。理事会、各専門委員会、事務局の役割や、資格認定部門、教育研修部門、国際交流部門などの機能を紹介。透明性のある協会運営を行っています。',
    keywords: '執事協会,組織体制,役員,理事会,委員会,運営',
    canonical: '/about/organization',
    hreflang: {
      ja: '/about/organization',
      en: '/en/about/organization',
      default: '/about/organization'
    }
  },
  '/about/activities': {
    title: '活動内容｜事業紹介 | 一般社団法人 全日本執事協会',
    description: '全日本執事協会が行う多様な活動の全体像をご紹介。資格認定事業、教育・研修事業、国際交流活動、情報発信など、執事文化の普及と質の向上を目指す取り組みを詳しく解説しています。',
    keywords: '執事協会,活動内容,事業,資格認定,教育研修,国際交流',
    canonical: '/about/activities',
    hreflang: {
      ja: '/about/activities',
      en: '/en/about/activities',
      default: '/about/activities'
    }
  },
  '/about/access': {
    title: 'アクセス・所在地 | 一般社団法人 全日本執事協会',
    description: '全日本執事協会本部の所在地とアクセス方法をご案内。〒135-0064 東京都江東区青海2丁目7−4、ゆりかもめ東京国際クルーズターミナル駅より徒歩約5分。ご来訪は完全予約制です。TEL 03-6824-4086',
    keywords: '執事協会,アクセス,所在地,東京都江東区,お問い合わせ,電話',
    canonical: '/about/access',
    hreflang: {
      ja: '/about/access',
      en: '/en/about/access',
      default: '/about/access'
    }
  },

  // 日本語ページ - 資格制度
  '/qualifications': {
    title: '執事資格制度｜資格取得ガイド | 一般社団法人 全日本執事協会',
    description: '全日本執事協会が認定する執事資格制度の全体像をご紹介。初級・中級・上級の3段階の基本資格と、6分野の専門資格を体系的に取得できます。未経験から上級者まで、キャリアに応じた資格取得をサポートします。',
    keywords: '執事資格,資格制度,資格取得,初級,中級,上級,専門資格',
    canonical: '/qualifications',
    hreflang: {
      ja: '/qualifications',
      en: '/en/qualifications',
      default: '/qualifications'
    }
  },
  '/qualifications/associate-butler': {
    title: '初級執事資格｜執事キャリアの第一歩 | 一般社団法人 全日本執事協会',
    description: '執事としての基礎知識と技能を証明する初級資格について詳しく解説。マナー、接客、コミュニケーションの基本を習得し、執事キャリアの第一歩を踏み出せます。未経験者も安心して取得可能な入門資格です。',
    keywords: '初級執事,Associate Butler,基礎資格,マナー,接客,未経験者',
    canonical: '/qualifications/associate-butler',
    hreflang: {
      ja: '/qualifications/associate-butler',
      en: '/en/qualifications/associate-butler',
      default: '/qualifications/associate-butler'
    }
  },
  '/qualifications/professional-butler': {
    title: '中級執事資格｜独立して活躍できる専門家認定 | 一般社団法人 全日本執事協会',
    description: '自立した執事として活躍するための中級資格をご紹介。ワインサービス、住居管理、来客対応など幅広い知識と経験を証明。高級ホテルや個人邸宅での活躍を目指す方に最適な専門家認定プログラムです。',
    keywords: '中級執事,Professional Butler,専門家,ワインサービス,住居管理,来客対応',
    canonical: '/qualifications/professional-butler',
    hreflang: {
      ja: '/qualifications/professional-butler',
      en: '/en/qualifications/professional-butler',
      default: '/qualifications/professional-butler'
    }
  },
  '/qualifications/master-butler': {
    title: '上級執事資格｜執事業界最高峰の認定 | 一般社団法人 全日本執事協会',
    description: '執事業界における最高峰の上級資格について詳しく解説。卓越した技術と知識、他の執事を指導できる能力を証明。国際的な文化理解、危機管理能力、洗練されたホスピタリティの哲学を体現する称号です。',
    keywords: '上級執事,Master Butler,最高峰,卓越した技術,指導力,国際的',
    canonical: '/qualifications/master-butler',
    hreflang: {
      ja: '/qualifications/master-butler',
      en: '/en/qualifications/master-butler',
      default: '/qualifications/master-butler'
    }
  },

  // 日本語ページ - セミナー
  '/seminars': {
    title: 'セミナー・イベント情報 | 一般社団法人 全日本執事協会',
    description: '実践的な執事スキルを身につける学びの場。執事基礎講座、ワインマナー、ビジネスマナー、ハウスキーピングなど多彩なセミナーを開催。初心者からプロフェッショナルまで、レベルに応じたプログラムを提供しています。',
    keywords: 'セミナー,イベント,執事基礎講座,ワインマナー,ビジネスマナー,ハウスキーピング',
    canonical: '/seminars',
    hreflang: {
      ja: '/seminars',
      en: '/en/seminars',
      default: '/seminars'
    }
  },
  '/seminars/corporate/communication-skills': {
    title: 'コミュニケーションスキル研修｜企業向けセミナー | 一般社団法人 全日本執事協会',
    description: '企業向けコミュニケーションスキル研修。効果的な対話技術、傾聴力、プレゼンテーション、交渉術を習得。ビジネスシーンで信頼関係を築き、円滑なコミュニケーションを実現するための実践的プログラムです。',
    keywords: 'コミュニケーションスキル,企業研修,対話技術,傾聴力,プレゼンテーション,交渉術',
    canonical: '/seminars/corporate/communication-skills',
    hreflang: {
      ja: '/seminars/corporate/communication-skills',
      en: '/en/seminars/corporate/communication-skills',
      default: '/seminars/corporate/communication-skills'
    }
  },
  '/seminars/corporate/crisis-management': {
    title: '危機管理研修｜企業向けセミナー | 一般社団法人 全日本執事協会',
    description: '企業向け危機管理研修。リスク予測、緊急時対応、クレーム処理、メディア対応を習得。予期せぬ事態に冷静に対処し、被害を最小限に抑えるための実践的な危機管理スキルを身につけます。',
    keywords: '危機管理,企業研修,リスク予測,緊急時対応,クレーム処理,メディア対応',
    canonical: '/seminars/corporate/crisis-management',
    hreflang: {
      ja: '/seminars/corporate/crisis-management',
      en: '/en/seminars/corporate/crisis-management',
      default: '/seminars/corporate/crisis-management'
    }
  },
  '/seminars/corporate/cross-cultural-understanding': {
    title: '異文化理解研修｜企業向けセミナー | 一般社団法人 全日本執事協会',
    description: '企業向け異文化理解研修。国際ビジネスマナー、文化的背景、宗教的配慮、グローバルコミュニケーションを習得。多様な文化背景を持つ人々と円滑に協働するための知識とスキルを身につけます。',
    keywords: '異文化理解,企業研修,国際ビジネスマナー,グローバルコミュニケーション,文化的背景',
    canonical: '/seminars/corporate/cross-cultural-understanding',
    hreflang: {
      ja: '/seminars/corporate/cross-cultural-understanding',
      en: '/en/seminars/corporate/cross-cultural-understanding',
      default: '/seminars/corporate/cross-cultural-understanding'
    }
  },
  '/seminars/corporate/hospitality-enhancement': {
    title: 'ホスピタリティ向上研修｜企業向けセミナー | 一般社団法人 全日本執事協会',
    description: '企業向けホスピタリティ向上研修。おもてなしの心、顧客満足度向上、サービスマインド、細やかな気配りを習得。お客様に感動を与える質の高いサービスを提供するための実践的プログラムです。',
    keywords: 'ホスピタリティ,企業研修,おもてなし,顧客満足度,サービスマインド,気配り',
    canonical: '/seminars/corporate/hospitality-enhancement',
    hreflang: {
      ja: '/seminars/corporate/hospitality-enhancement',
      en: '/en/seminars/corporate/hospitality-enhancement',
      default: '/seminars/corporate/hospitality-enhancement'
    }
  },
  '/seminars/corporate/schedule-management': {
    title: 'スケジュール管理研修｜企業向けセミナー | 一般社団法人 全日本執事協会',
    description: '企業向けスケジュール管理研修。時間管理、優先順位付け、タスク整理、効率的な業務遂行を習得。限られた時間を最大限に活用し、生産性を向上させるための実践的なスキルを身につけます。',
    keywords: 'スケジュール管理,企業研修,時間管理,優先順位,タスク整理,生産性向上',
    canonical: '/seminars/corporate/schedule-management',
    hreflang: {
      ja: '/seminars/corporate/schedule-management',
      en: '/en/seminars/corporate/schedule-management',
      default: '/seminars/corporate/schedule-management'
    }
  },
  '/seminars/corporate/team-leadership': {
    title: 'チームリーダーシップ研修｜企業向けセミナー | 一般社団法人 全日本執事協会',
    description: '企業向けチームリーダーシップ研修。リーダーシップスキル、チームビルディング、モチベーション管理、目標達成を習得。チームを効果的に導き、組織の成果を最大化するための実践的プログラムです。',
    keywords: 'チームリーダーシップ,企業研修,リーダーシップスキル,チームビルディング,モチベーション管理',
    canonical: '/seminars/corporate/team-leadership',
    hreflang: {
      ja: '/seminars/corporate/team-leadership',
      en: '/en/seminars/corporate/team-leadership',
      default: '/seminars/corporate/team-leadership'
    }
  },

  // 日本語ページ - サービス
  '/services/butler': {
    title: '執事サービス｜プロフェッショナル執事派遣 | 一般社団法人 全日本執事協会',
    description: '経験豊富なプロフェッショナル執事を派遣。日常生活のサポートからVIP対応まで、お客様のニーズに合わせた質の高いサービスを提供。信頼と実績の執事サービスで、上質な暮らしをサポートします。',
    keywords: '執事サービス,執事派遣,プロフェッショナル,VIP対応,日常生活サポート',
    canonical: '/services/butler',
    hreflang: {
      ja: '/services/butler',
      en: '/en/services/butler',
      default: '/services/butler'
    }
  },
  '/services/footman': {
    title: 'フットマンサービス｜接客・配膳専門スタッフ | 一般社団法人 全日本執事協会',
    description: 'フットマンサービスのご案内。パーティーやイベントでの接客、配膳、ゲスト対応を専門とするスタッフを派遣。洗練されたサービスで、特別な時間を演出します。',
    keywords: 'フットマン,接客,配膳,パーティー,イベント,ゲスト対応',
    canonical: '/services/footman',
    hreflang: {
      ja: '/services/footman',
      en: '/en/services/footman',
      default: '/services/footman'
    }
  },
  '/services/house-steward': {
    title: 'ハウススチュワードサービス｜邸宅管理専門スタッフ | 一般社団法人 全日本執事協会',
    description: 'ハウススチュワードサービスのご案内。邸宅の清掃、整理整頓、メンテナンス、セキュリティ管理を専門とするスタッフを派遣。快適で安全な住環境を維持します。',
    keywords: 'ハウススチュワード,邸宅管理,清掃,メンテナンス,セキュリティ管理',
    canonical: '/services/house-steward',
    hreflang: {
      ja: '/services/house-steward',
      en: '/en/services/house-steward',
      default: '/services/house-steward'
    }
  },
  '/services/second-steward': {
    title: 'セカンドスチュワードサービス｜執事補佐スタッフ | 一般社団法人 全日本執事協会',
    description: 'セカンドスチュワードサービスのご案内。執事の補佐として、日常業務のサポート、来客対応、事務作業を担当するスタッフを派遣。執事と連携し、円滑なサービス提供を実現します。',
    keywords: 'セカンドスチュワード,執事補佐,日常業務,来客対応,事務作業',
    canonical: '/services/second-steward',
    hreflang: {
      ja: '/services/second-steward',
      en: '/en/services/second-steward',
      default: '/services/second-steward'
    }
  },

  // 日本語ページ - その他
  '/butler-services': {
    title: '執事派遣サービス｜THE BUTLER | 一般社団法人 全日本執事協会',
    description: '全日本執事協会が運営する執事人材サービス「THE BUTLER」。3時間24,000円から24時間体制まで、ニーズに合わせた執事サービスを提供。経験豊富なプロフェッショナル執事が日常生活からVIP対応まで幅広くサポートします。',
    keywords: '執事派遣,THE BUTLER,執事サービス,プロフェッショナル,VIP対応,日常生活サポート',
    canonical: '/butler-services',
    hreflang: {
      ja: '/butler-services',
      en: '/en/butler-services',
      default: '/butler-services'
    }
  },
  '/news': {
    title: 'ニュース・お知らせ | 一般社団法人 全日本執事協会',
    description: '全日本執事協会の最新情報と執事業界の動向をお届け。協会からの重要なお知らせ、資格試験情報、セミナー開催レポート、国際交流活動の報告など、執事に関する最新ニュースを定期的に更新しています。',
    keywords: 'ニュース,お知らせ,執事業界,最新情報,資格試験,セミナー',
    canonical: '/news',
    hreflang: {
      ja: '/news',
      en: '/en/news',
      default: '/news'
    }
  },
  '/membership': {
    title: '会員募集・入会案内 | 一般社団法人 全日本執事協会',
    description: '全日本執事協会の会員制度と入会案内。正会員・準会員・法人会員の3種類から選択可能。資格試験割引、セミナー優先予約、会員限定イベント、国際交流プログラムなど充実の特典でキャリアアップをサポートします。',
    keywords: '会員募集,入会案内,正会員,準会員,法人会員,資格試験割引',
    canonical: '/membership',
    hreflang: {
      ja: '/membership',
      en: '/en/membership',
      default: '/membership'
    }
  },
  '/contact': {
    title: 'お問い合わせ・資料請求 | 一般社団法人 全日本執事協会',
    description: '資格取得、セミナー、会員登録に関するご質問はお気軽にお問い合わせください。東京都江東区青海、東京国際クルーズターミナル駅徒歩5分。電話、メール、ご来訪での相談を受付中。TEL 03-6824-4086',
    keywords: 'お問い合わせ,資料請求,相談,電話,メール,東京都江東区',
    canonical: '/contact',
    hreflang: {
      ja: '/contact',
      en: '/en/contact',
      default: '/contact'
    }
  },

  // 英語ページ - トップページ
  '/en': {
    title: 'Japan Butler Association | Official Website',
    description: 'Japan Butler Association is the official organization dedicated to promoting butler culture and professional development. We offer butler certification, specialized seminars, and international exchange programs to advance the art of butler service in Japan.',
    keywords: 'butler association, butler certification, butler training, hospitality, Japan',
    canonical: '/en',
    hreflang: {
      ja: '/',
      en: '/en',
      default: '/'
    }
  },

  // 英語ページ - 協会について
  '/en/about': {
    title: 'About Us | Mission & Activities | Japan Butler Association',
    description: 'Learn about Japan Butler Association\'s mission and activities. We are dedicated to promoting butler culture and training high-quality service professionals through certification programs, educational training, and international exchange.',
    keywords: 'about, mission, activities, butler association, training, certification',
    canonical: '/en/about',
    hreflang: {
      ja: '/about',
      en: '/en/about',
      default: '/about'
    }
  },
  '/en/about/overview': {
    title: 'Association Overview | Purpose & Business | Japan Butler Association',
    description: 'Introduction to the establishment background and organizational overview of Japan Butler Association. As the governing body of the butler industry, we establish certification systems, provide specialized education, and build international networks.',
    keywords: 'overview, organization, purpose, business, butler association',
    canonical: '/en/about/overview',
    hreflang: {
      ja: '/about/overview',
      en: '/en/about/overview',
      default: '/about/overview'
    }
  },
  '/en/about/mission': {
    title: 'Mission & Philosophy | Promoting Butler Culture | Japan Butler Association',
    description: 'Detailed introduction to the mission and philosophy of Japan Butler Association. Our three pillars are: creating refined service culture, pursuing professionalism as butlers, and establishing unique Japanese butler culture.',
    keywords: 'mission, philosophy, butler culture, professionalism, service culture',
    canonical: '/en/about/mission',
    hreflang: {
      ja: '/about/mission',
      en: '/en/about/mission',
      default: '/about/mission'
    }
  },
  '/en/about/history': {
    title: 'History & Timeline | Association Journey | Japan Butler Association',
    description: 'Timeline of Japan Butler Association from establishment to present. View the detailed history including establishment background, certification system development process, and expansion of international activities.',
    keywords: 'history, timeline, establishment, development, butler association',
    canonical: '/en/about/history',
    hreflang: {
      ja: '/about/history',
      en: '/en/about/history',
      default: '/about/history'
    }
  },
  '/en/about/organization': {
    title: 'Organization & Board Members | Japan Butler Association',
    description: 'Information about the organizational structure and management system of Japan Butler Association. Introduction to the roles of the board, specialized committees, and secretariat, including certification, education, and international exchange departments.',
    keywords: 'organization, board members, management, committees, departments',
    canonical: '/en/about/organization',
    hreflang: {
      ja: '/about/organization',
      en: '/en/about/organization',
      default: '/about/organization'
    }
  },
  '/en/about/activities': {
    title: 'Activities & Business Introduction | Japan Butler Association',
    description: 'Overview of diverse activities conducted by Japan Butler Association. Detailed explanation of initiatives aimed at promoting butler culture and improving quality, including certification, education and training, international exchange, and information dissemination.',
    keywords: 'activities, business, certification, education, international exchange',
    canonical: '/en/about/activities',
    hreflang: {
      ja: '/about/activities',
      en: '/en/about/activities',
      default: '/about/activities'
    }
  },
  '/en/about/access': {
    title: 'Access & Location | Japan Butler Association',
    description: 'Location and access information to Japan Butler Association headquarters. Located in Koto-ku, Tokyo, 5 minutes walk from Tokyo International Cruise Terminal Station. Inquiries and tour reservations accepted. TEL 03-6824-4086',
    keywords: 'access, location, Tokyo, Koto-ku, contact, phone',
    canonical: '/en/about/access',
    hreflang: {
      ja: '/about/access',
      en: '/en/about/access',
      default: '/about/access'
    }
  },

  // 英語ページ - 資格制度
  '/en/qualifications': {
    title: 'Butler Certification Programs | Qualification Guide | Japan Butler Association',
    description: 'Overview of butler certification system certified by Japan Butler Association. Systematically acquire three levels of basic qualifications and six specialized fields. Support qualification acquisition according to your career stage, from beginners to advanced professionals.',
    keywords: 'butler certification, qualification system, beginner, intermediate, advanced, specialized',
    canonical: '/en/qualifications',
    hreflang: {
      ja: '/qualifications',
      en: '/en/qualifications',
      default: '/qualifications'
    }
  },
  '/en/qualifications/associate-butler': {
    title: 'Associate Butler Certification | Beginner Level | Japan Butler Association',
    description: 'Detailed explanation of beginner certification proving basic butler knowledge and skills. Learn fundamentals of manners, customer service, and communication to take the first step in your butler career. Entry-level qualification suitable for beginners.',
    keywords: 'Associate Butler, beginner certification, fundamentals, manners, customer service',
    canonical: '/en/qualifications/associate-butler',
    hreflang: {
      ja: '/qualifications/associate-butler',
      en: '/en/qualifications/associate-butler',
      default: '/qualifications/associate-butler'
    }
  },
  '/en/qualifications/professional-butler': {
    title: 'Professional Butler Certification | Intermediate Level | Japan Butler Association',
    description: 'Introduction to intermediate certification for working as an independent butler. Proves extensive knowledge and experience including wine service, residence management, and guest reception. Professional certification program ideal for those aiming to work in luxury hotels or private residences.',
    keywords: 'Professional Butler, intermediate certification, wine service, residence management, guest reception',
    canonical: '/en/qualifications/professional-butler',
    hreflang: {
      ja: '/qualifications/professional-butler',
      en: '/en/qualifications/professional-butler',
      default: '/qualifications/professional-butler'
    }
  },
  '/en/qualifications/master-butler': {
    title: 'Master Butler Certification | Advanced Level | Japan Butler Association',
    description: 'Detailed explanation of the highest advanced certification in the butler industry. Proves exceptional skills and knowledge, and ability to guide other butlers. A title embodying international cultural understanding, crisis management ability, and refined hospitality philosophy.',
    keywords: 'Master Butler, advanced certification, highest level, exceptional skills, leadership',
    canonical: '/en/qualifications/master-butler',
    hreflang: {
      ja: '/qualifications/master-butler',
      en: '/en/qualifications/master-butler',
      default: '/qualifications/master-butler'
    }
  },

  // 英語ページ - セミナー
  '/en/seminars': {
    title: 'Seminars & Events | Japan Butler Association',
    description: 'Learning opportunities to acquire practical butler skills. Offering butler basics, wine manners, business manners, housekeeping, and various other seminars. Programs tailored to all levels from beginners to professionals.',
    keywords: 'seminars, events, butler basics, wine manners, business manners, housekeeping',
    canonical: '/en/seminars',
    hreflang: {
      ja: '/seminars',
      en: '/en/seminars',
      default: '/seminars'
    }
  },
  '/en/seminars/corporate/communication-skills': {
    title: 'Communication Skills Training | Corporate Seminars | Japan Butler Association',
    description: 'Corporate communication skills training. Learn effective dialogue techniques, listening skills, presentation, and negotiation. Practical program to build trust and achieve smooth communication in business settings.',
    keywords: 'communication skills, corporate training, dialogue, listening, presentation, negotiation',
    canonical: '/en/seminars/corporate/communication-skills',
    hreflang: {
      ja: '/seminars/corporate/communication-skills',
      en: '/en/seminars/corporate/communication-skills',
      default: '/seminars/corporate/communication-skills'
    }
  },
  '/en/seminars/corporate/crisis-management': {
    title: 'Crisis Management Training | Corporate Seminars | Japan Butler Association',
    description: 'Corporate crisis management training. Learn risk prediction, emergency response, complaint handling, and media relations. Acquire practical crisis management skills to respond calmly to unexpected situations and minimize damage.',
    keywords: 'crisis management, corporate training, risk prediction, emergency response, complaint handling',
    canonical: '/en/seminars/corporate/crisis-management',
    hreflang: {
      ja: '/seminars/corporate/crisis-management',
      en: '/en/seminars/corporate/crisis-management',
      default: '/seminars/corporate/crisis-management'
    }
  },
  '/en/seminars/corporate/cross-cultural-understanding': {
    title: 'Cross-Cultural Understanding Training | Corporate Seminars | Japan Butler Association',
    description: 'Corporate cross-cultural understanding training. Learn international business manners, cultural backgrounds, religious considerations, and global communication. Acquire knowledge and skills to collaborate smoothly with people from diverse cultural backgrounds.',
    keywords: 'cross-cultural understanding, corporate training, international business manners, global communication',
    canonical: '/en/seminars/corporate/cross-cultural-understanding',
    hreflang: {
      ja: '/seminars/corporate/cross-cultural-understanding',
      en: '/en/seminars/corporate/cross-cultural-understanding',
      default: '/seminars/corporate/cross-cultural-understanding'
    }
  },
  '/en/seminars/corporate/hospitality-enhancement': {
    title: 'Hospitality Enhancement Training | Corporate Seminars | Japan Butler Association',
    description: 'Corporate hospitality enhancement training. Learn spirit of hospitality, customer satisfaction improvement, service mindset, and attentive care. Practical program to provide high-quality service that impresses customers.',
    keywords: 'hospitality, corporate training, customer satisfaction, service mindset, attentive care',
    canonical: '/en/seminars/corporate/hospitality-enhancement',
    hreflang: {
      ja: '/seminars/corporate/hospitality-enhancement',
      en: '/en/seminars/corporate/hospitality-enhancement',
      default: '/seminars/corporate/hospitality-enhancement'
    }
  },
  '/en/seminars/corporate/schedule-management': {
    title: 'Schedule Management Training | Corporate Seminars | Japan Butler Association',
    description: 'Corporate schedule management training. Learn time management, prioritization, task organization, and efficient work execution. Acquire practical skills to maximize limited time and improve productivity.',
    keywords: 'schedule management, corporate training, time management, prioritization, productivity',
    canonical: '/en/seminars/corporate/schedule-management',
    hreflang: {
      ja: '/seminars/corporate/schedule-management',
      en: '/en/seminars/corporate/schedule-management',
      default: '/seminars/corporate/schedule-management'
    }
  },
  '/en/seminars/corporate/team-leadership': {
    title: 'Team Leadership Training | Corporate Seminars | Japan Butler Association',
    description: 'Corporate team leadership training. Learn leadership skills, team building, motivation management, and goal achievement. Practical program to effectively lead teams and maximize organizational results.',
    keywords: 'team leadership, corporate training, leadership skills, team building, motivation',
    canonical: '/en/seminars/corporate/team-leadership',
    hreflang: {
      ja: '/seminars/corporate/team-leadership',
      en: '/en/seminars/corporate/team-leadership',
      default: '/seminars/corporate/team-leadership'
    }
  },

  // 英語ページ - サービス
  '/en/services/butler': {
    title: 'Butler Service | Professional Butler Dispatch | Japan Butler Association',
    description: 'Dispatch of experienced professional butlers. Providing high-quality services tailored to customer needs, from daily life support to VIP services. Support quality living with trusted and proven butler services.',
    keywords: 'butler service, butler dispatch, professional, VIP service, daily life support',
    canonical: '/en/services/butler',
    hreflang: {
      ja: '/services/butler',
      en: '/en/services/butler',
      default: '/services/butler'
    }
  },
  '/en/services/footman': {
    title: 'Footman Service | Customer Service & Serving Staff | Japan Butler Association',
    description: 'Introduction to footman service. Dispatch staff specializing in customer service, serving, and guest reception at parties and events. Create special moments with refined service.',
    keywords: 'footman, customer service, serving, party, event, guest reception',
    canonical: '/en/services/footman',
    hreflang: {
      ja: '/services/footman',
      en: '/en/services/footman',
      default: '/services/footman'
    }
  },
  '/en/services/second-steward': {
    title: 'Second Steward Service | Butler Assistant Staff | Japan Butler Association',
    description: 'Introduction to second steward service. Dispatch staff who assist butlers with daily tasks, guest reception, and administrative work. Achieve smooth service delivery in coordination with butlers.',
    keywords: 'second steward, butler assistant, daily tasks, guest reception, administrative work',
    canonical: '/en/services/second-steward',
    hreflang: {
      ja: '/services/second-steward',
      en: '/en/services/second-steward',
      default: '/services/second-steward'
    }
  },

  // 英語ページ - その他
  '/en/butler-services': {
    title: 'Butler Dispatch Service | THE BUTLER | Japan Butler Association',
    description: 'THE BUTLER butler personnel service operated by Japan Butler Association. From 3 hours at ¥24,000 to 24-hour service, we provide butler services tailored to your needs. Experienced professional butlers support everything from daily life to VIP services.',
    keywords: 'butler dispatch, THE BUTLER, butler service, professional, VIP service, daily life support',
    canonical: '/en/butler-services',
    hreflang: {
      ja: '/butler-services',
      en: '/en/butler-services',
      default: '/butler-services'
    }
  },
  '/en/news': {
    title: 'News & Updates | Japan Butler Association',
    description: 'Stay informed with the latest from Japan Butler Association and the butler industry. Read announcements, certification updates, seminar reports, and international exchange activities. Updated regularly with butler news.',
    keywords: 'news, announcements, butler industry, latest information, qualification exams, seminars',
    canonical: '/en/news',
    hreflang: {
      ja: '/news',
      en: '/en/news',
      default: '/news'
    }
  },
  '/en/membership': {
    title: 'Membership Information | Japan Butler Association',
    description: 'Join Japan Butler Association. Choose from Regular, Associate, or Corporate membership. Enjoy exam discounts, seminar priority booking, exclusive events, and international exchange programs to advance your butler career.',
    keywords: 'membership, enrollment, regular member, associate member, corporate member, benefits',
    canonical: '/en/membership',
    hreflang: {
      ja: '/membership',
      en: '/en/membership',
      default: '/membership'
    }
  },
  '/en/contact': {
    title: 'Contact Us | Japan Butler Association',
    description: 'Contact us for inquiries about certification, seminars, and membership. Located in Koto-ku, Tokyo, five minutes from Tokyo International Cruise Terminal Station. Phone, email, and visit consultations available. TEL 03-6824-4086',
    keywords: 'contact, information request, consultation, phone, email, Tokyo Koto-ku',
    canonical: '/en/contact',
    hreflang: {
      ja: '/contact',
      en: '/en/contact',
      default: '/contact'
    }
  },

  // ニュース記事（日本語）
  '/news/raffles-london-campaign': {
    title: 'ラッフルズ・ロンドン、執事サービスキャンペーン開始 | ニュース | 全日本執事協会',
    description: 'ラッフルズ・ロンドンが新たな執事サービスキャンペーンを開始。ラグジュアリーホテル業界における執事サービスの最新動向と、その意義について詳しく解説します。',
    keywords: 'ラッフルズ,ロンドン,執事サービス,キャンペーン,ラグジュアリーホテル',
    canonical: '/news/raffles-london-campaign',
    hreflang: {
      ja: '/news/raffles-london-campaign',
      en: '/en/news/raffles-london-campaign',
      default: '/news/raffles-london-campaign'
    }
  },
  '/news/downton-abbey-grand-finale': {
    title: 'ダウントン・アベー、グランドフィナーレで執事文化を世界に発信 | ニュース | 全日本執事協会',
    description: '人気ドラマ「ダウントン・アベー」のグランドフィナーレが執事文化に与えた影響を分析。英国式執事の伝統と現代における執事の役割について詳しく解説します。',
    keywords: 'ダウントン・アベー,執事文化,英国式,ドラマ,伝統',
    canonical: '/news/downton-abbey-grand-finale',
    hreflang: {
      ja: '/news/downton-abbey-grand-finale',
      en: '/en/news/downton-abbey-grand-finale',
      default: '/news/downton-abbey-grand-finale'
    }
  },
  '/news/raffles-butler-campaign': {
    title: 'ラッフルズホテル、執事サービス強化キャンペーン | ニュース | 全日本執事協会',
    description: 'ラッフルズホテルが執事サービスを強化するキャンペーンを展開。世界的なラグジュアリーホテルチェーンにおける執事サービスの重要性と今後の展望について解説します。',
    keywords: 'ラッフルズホテル,執事サービス,キャンペーン,ラグジュアリーホテル',
    canonical: '/news/raffles-butler-campaign',
    hreflang: {
      ja: '/news/raffles-butler-campaign',
      en: '/en/news/raffles-butler-campaign',
      default: '/news/raffles-butler-campaign'
    }
  },
  '/news/apple-robot-butler': {
    title: 'Apple、ロボット執事の開発に着手 | ニュース | 全日本執事協会',
    description: 'Appleがロボット執事の開発を開始。AI技術とロボティクスの融合により、執事サービスの未来がどのように変わるのか、その可能性と課題について詳しく解説します。',
    keywords: 'Apple,ロボット執事,AI,ロボティクス,未来',
    canonical: '/news/apple-robot-butler',
    hreflang: {
      ja: '/news/apple-robot-butler',
      en: '/en/news/apple-robot-butler',
      default: '/news/apple-robot-butler'
    }
  },
  '/news/raffles-sentosa-butler-package': {
    title: 'ラッフルズ・セントーサ、執事付き宿泊パッケージ発表 | ニュース | 全日本執事協会',
    description: 'ラッフルズ・セントーサが執事付き宿泊パッケージを発表。シンガポールのラグジュアリーリゾートにおける執事サービスの特徴と、その魅力について詳しく紹介します。',
    keywords: 'ラッフルズ・セントーサ,執事付き,宿泊パッケージ,シンガポール,リゾート',
    canonical: '/news/raffles-sentosa-butler-package',
    hreflang: {
      ja: '/news/raffles-sentosa-butler-package',
      en: '/en/news/raffles-sentosa-butler-package',
      default: '/news/raffles-sentosa-butler-package'
    }
  },
  '/news/ai-hospitality-revolution': {
    title: 'AI技術がホスピタリティ業界に革命をもたらす | ニュース | 全日本執事協会',
    description: 'AI技術がホスピタリティ業界に与える影響を分析。執事サービスにおけるAI活用の可能性と、人間の執事との共存について詳しく解説します。',
    keywords: 'AI,ホスピタリティ,革命,執事サービス,技術',
    canonical: '/news/ai-hospitality-revolution',
    hreflang: {
      ja: '/news/ai-hospitality-revolution',
      en: '/en/news/ai-hospitality-revolution',
      default: '/news/ai-hospitality-revolution'
    }
  },
  '/news/grant-harrold-royal-butler-memoir': {
    title: '元英国王室執事グラント・ハロルド、回顧録を出版 | ニュース | 全日本執事協会',
    description: '元英国王室執事グラント・ハロルドが回顧録を出版。王室での執事経験から学ぶ、真のホスピタリティとプロフェッショナリズムについて詳しく紹介します。',
    keywords: 'グラント・ハロルド,英国王室,執事,回顧録,ホスピタリティ',
    canonical: '/news/grant-harrold-royal-butler-memoir',
    hreflang: {
      ja: '/news/grant-harrold-royal-butler-memoir',
      en: '/en/news/grant-harrold-royal-butler-memoir',
      default: '/news/grant-harrold-royal-butler-memoir'
    }
  },
  '/news/swiss-luxury-travel-ski-butler': {
    title: 'スイスの高級旅行会社、スキー執事サービスを開始 | ニュース | 全日本執事協会',
    description: 'スイスの高級旅行会社が新たにスキー執事サービスを開始。ウィンタースポーツにおける執事サービスの可能性と、その特徴について詳しく解説します。',
    keywords: 'スイス,高級旅行,スキー執事,ウィンタースポーツ,サービス',
    canonical: '/news/swiss-luxury-travel-ski-butler',
    hreflang: {
      ja: '/news/swiss-luxury-travel-ski-butler',
      en: '/en/news/swiss-luxury-travel-ski-butler',
      default: '/news/swiss-luxury-travel-ski-butler'
    }
  },
  '/news/hong-kong-butler-ai-profitability': {
    title: '香港の執事サービス企業、AI導入で収益性向上 | ニュース | 全日本執事協会',
    description: '香港の執事サービス企業がAI技術を導入し収益性を向上。執事業界におけるデジタル化の進展と、その効果について詳しく分析します。',
    keywords: '香港,執事サービス,AI,収益性,デジタル化',
    canonical: '/news/hong-kong-butler-ai-profitability',
    hreflang: {
      ja: '/news/hong-kong-butler-ai-profitability',
      en: '/en/news/hong-kong-butler-ai-profitability',
      default: '/news/hong-kong-butler-ai-profitability'
    }
  },
  '/news/tiba-global-award-five-years': {
    title: 'TIBA、グローバル執事アワードで5年連続受賞 | ニュース | 全日本執事協会',
    description: 'The International Butler Academyがグローバル執事アワードで5年連続受賞。世界的な執事教育機関の取り組みと、その成功の秘訣について詳しく紹介します。',
    keywords: 'TIBA,グローバル執事アワード,受賞,執事教育,国際',
    canonical: '/news/tiba-global-award-five-years',
    hreflang: {
      ja: '/news/tiba-global-award-five-years',
      en: '/en/news/tiba-global-award-five-years',
      default: '/news/tiba-global-award-five-years'
    }
  },
  '/news/china-butler-demand-surge': {
    title: '中国で執事需要が急増、富裕層向けサービス拡大 | ニュース | 全日本執事協会',
    description: '中国における執事需要の急増を分析。富裕層の増加に伴う執事サービス市場の拡大と、その背景について詳しく解説します。',
    keywords: '中国,執事需要,富裕層,サービス拡大,市場',
    canonical: '/news/china-butler-demand-surge',
    hreflang: {
      ja: '/news/china-butler-demand-surge',
      en: '/en/news/china-butler-demand-surge',
      default: '/news/china-butler-demand-surge'
    }
  },
  '/news/jumeirah-burj-al-arab-25th-anniversary': {
    title: 'ジュメイラ・バージュ・アル・アラブ、25周年記念で執事サービス強化 | ニュース | 全日本執事協会',
    description: 'ドバイの象徴的ホテル、ジュメイラ・バージュ・アル・アラブが25周年を迎え執事サービスを強化。世界最高峰のホテルにおける執事サービスの進化について詳しく紹介します。',
    keywords: 'ジュメイラ,バージュ・アル・アラブ,25周年,執事サービス,ドバイ',
    canonical: '/news/jumeirah-burj-al-arab-25th-anniversary',
    hreflang: {
      ja: '/news/jumeirah-burj-al-arab-25th-anniversary',
      en: '/en/news/jumeirah-burj-al-arab-25th-anniversary',
      default: '/news/jumeirah-burj-al-arab-25th-anniversary'
    }
  },
  '/news/global-butler-shortage-salary-surge': {
    title: '世界的な執事不足で給与が急騰、業界に変化の兆し | ニュース | 全日本執事協会',
    description: '世界的な執事不足により給与が急騰している現状を分析。執事業界における人材確保の課題と、今後の展望について詳しく解説します。',
    keywords: '執事不足,給与急騰,人材確保,業界動向,世界',
    canonical: '/news/global-butler-shortage-salary-surge',
    hreflang: {
      ja: '/news/global-butler-shortage-salary-surge',
      en: '/en/news/global-butler-shortage-salary-surge',
      default: '/news/global-butler-shortage-salary-surge'
    }
  },
  '/news/weave-robotics-isaac-robot-butler': {
    title: 'Weave Robotics、AI執事ロボット「Isaac」を発表 | ニュース | 全日本執事協会',
    description: 'Weave RoboticsがAI執事ロボット「Isaac」を発表。最新のロボティクス技術とAIが融合した執事ロボットの可能性と、執事業界への影響について詳しく解説します。',
    keywords: 'Weave Robotics,Isaac,AI執事ロボット,ロボティクス,技術',
    canonical: '/news/weave-robotics-isaac-robot-butler',
    hreflang: {
      ja: '/news/weave-robotics-isaac-robot-butler',
      en: '/en/news/weave-robotics-isaac-robot-butler',
      default: '/news/weave-robotics-isaac-robot-butler'
    }
  },

  // ニュース記事（英語）
  '/en/news/raffles-london-campaign': {
    title: 'Raffles London Launches Butler Service Campaign | News | Japan Butler Association',
    description: 'Raffles London launches new butler service campaign. Detailed explanation of the latest trends in butler services in the luxury hotel industry and their significance.',
    keywords: 'Raffles, London, butler service, campaign, luxury hotel',
    canonical: '/en/news/raffles-london-campaign',
    hreflang: {
      ja: '/news/raffles-london-campaign',
      en: '/en/news/raffles-london-campaign',
      default: '/news/raffles-london-campaign'
    }
  },
  '/en/news/downton-abbey-grand-finale': {
    title: 'Downton Abbey Grand Finale Promotes Butler Culture Worldwide | News | Japan Butler Association',
    description: 'Analysis of the impact of the popular drama "Downton Abbey" grand finale on butler culture. Detailed explanation of British butler traditions and the role of butlers in modern times.',
    keywords: 'Downton Abbey, butler culture, British style, drama, tradition',
    canonical: '/en/news/downton-abbey-grand-finale',
    hreflang: {
      ja: '/news/downton-abbey-grand-finale',
      en: '/en/news/downton-abbey-grand-finale',
      default: '/news/downton-abbey-grand-finale'
    }
  },
  '/en/news/raffles-butler-campaign': {
    title: 'Raffles Hotel Butler Service Enhancement Campaign | News | Japan Butler Association',
    description: 'Raffles Hotel launches campaign to enhance butler services. Explanation of the importance of butler services in global luxury hotel chains and future prospects.',
    keywords: 'Raffles Hotel, butler service, campaign, luxury hotel',
    canonical: '/en/news/raffles-butler-campaign',
    hreflang: {
      ja: '/news/raffles-butler-campaign',
      en: '/en/news/raffles-butler-campaign',
      default: '/news/raffles-butler-campaign'
    }
  },
  '/en/news/apple-robot-butler': {
    title: 'Apple Begins Development of Robot Butler | News | Japan Butler Association',
    description: 'Apple begins development of robot butler. Detailed explanation of how the fusion of AI technology and robotics will change the future of butler services, including possibilities and challenges.',
    keywords: 'Apple, robot butler, AI, robotics, future',
    canonical: '/en/news/apple-robot-butler',
    hreflang: {
      ja: '/news/apple-robot-butler',
      en: '/en/news/apple-robot-butler',
      default: '/news/apple-robot-butler'
    }
  },
  '/en/news/raffles-sentosa-butler-package': {
    title: 'Raffles Sentosa Announces Butler-Inclusive Package | News | Japan Butler Association',
    description: 'Raffles Sentosa announces butler-inclusive accommodation package. Detailed introduction to the characteristics and appeal of butler services at Singapore\'s luxury resort.',
    keywords: 'Raffles Sentosa, butler-inclusive, accommodation package, Singapore, resort',
    canonical: '/en/news/raffles-sentosa-butler-package',
    hreflang: {
      ja: '/news/raffles-sentosa-butler-package',
      en: '/en/news/raffles-sentosa-butler-package',
      default: '/news/raffles-sentosa-butler-package'
    }
  },
  '/en/news/ai-hospitality-revolution': {
    title: 'AI Technology Revolutionizes Hospitality Industry | News | Japan Butler Association',
    description: 'Analysis of the impact of AI technology on the hospitality industry. Detailed explanation of the possibilities of AI utilization in butler services and coexistence with human butlers.',
    keywords: 'AI, hospitality, revolution, butler service, technology',
    canonical: '/en/news/ai-hospitality-revolution',
    hreflang: {
      ja: '/news/ai-hospitality-revolution',
      en: '/en/news/ai-hospitality-revolution',
      default: '/news/ai-hospitality-revolution'
    }
  },
  '/en/news/grant-harrold-royal-butler-memoir': {
    title: 'Former Royal Butler Grant Harrold Publishes Memoir | News | Japan Butler Association',
    description: 'Former royal butler Grant Harrold publishes memoir. Detailed introduction to true hospitality and professionalism learned from royal butler experience.',
    keywords: 'Grant Harrold, royal butler, memoir, hospitality, professionalism',
    canonical: '/en/news/grant-harrold-royal-butler-memoir',
    hreflang: {
      ja: '/news/grant-harrold-royal-butler-memoir',
      en: '/en/news/grant-harrold-royal-butler-memoir',
      default: '/news/grant-harrold-royal-butler-memoir'
    }
  },
  '/en/news/swiss-luxury-travel-ski-butler': {
    title: 'Swiss Luxury Travel Company Launches Ski Butler Service | News | Japan Butler Association',
    description: 'Swiss luxury travel company launches new ski butler service. Detailed explanation of the possibilities and characteristics of butler services in winter sports.',
    keywords: 'Swiss, luxury travel, ski butler, winter sports, service',
    canonical: '/en/news/swiss-luxury-travel-ski-butler',
    hreflang: {
      ja: '/news/swiss-luxury-travel-ski-butler',
      en: '/en/news/swiss-luxury-travel-ski-butler',
      default: '/news/swiss-luxury-travel-ski-butler'
    }
  },
  '/en/news/hong-kong-butler-ai-profitability': {
    title: 'Hong Kong Butler Service Company Improves Profitability with AI | News | Japan Butler Association',
    description: 'Hong Kong butler service company improves profitability by introducing AI technology. Detailed analysis of the progress of digitalization in the butler industry and its effects.',
    keywords: 'Hong Kong, butler service, AI, profitability, digitalization',
    canonical: '/en/news/hong-kong-butler-ai-profitability',
    hreflang: {
      ja: '/news/hong-kong-butler-ai-profitability',
      en: '/en/news/hong-kong-butler-ai-profitability',
      default: '/news/hong-kong-butler-ai-profitability'
    }
  },
  '/en/news/tiba-global-award-five-years': {
    title: 'TIBA Wins Global Butler Award for Fifth Consecutive Year | News | Japan Butler Association',
    description: 'The International Butler Academy wins Global Butler Award for fifth consecutive year. Detailed introduction to the initiatives of the world\'s leading butler education institution and the secrets of its success.',
    keywords: 'TIBA, Global Butler Award, award, butler education, international',
    canonical: '/en/news/tiba-global-award-five-years',
    hreflang: {
      ja: '/news/tiba-global-award-five-years',
      en: '/en/news/tiba-global-award-five-years',
      default: '/news/tiba-global-award-five-years'
    }
  },
  '/en/news/china-butler-demand-surge': {
    title: 'Butler Demand Surges in China, Expanding Services for Wealthy | News | Japan Butler Association',
    description: 'Analysis of the surge in butler demand in China. Detailed explanation of the expansion of the butler service market accompanying the increase in wealthy individuals and its background.',
    keywords: 'China, butler demand, wealthy, service expansion, market',
    canonical: '/en/news/china-butler-demand-surge',
    hreflang: {
      ja: '/news/china-butler-demand-surge',
      en: '/en/news/china-butler-demand-surge',
      default: '/news/china-butler-demand-surge'
    }
  },
  '/en/news/jumeirah-burj-al-arab-25th-anniversary': {
    title: 'Jumeirah Burj Al Arab Enhances Butler Service for 25th Anniversary | News | Japan Butler Association',
    description: 'Dubai\'s iconic hotel, Jumeirah Burj Al Arab, celebrates 25th anniversary and enhances butler services. Detailed introduction to the evolution of butler services at the world\'s finest hotel.',
    keywords: 'Jumeirah, Burj Al Arab, 25th anniversary, butler service, Dubai',
    canonical: '/en/news/jumeirah-burj-al-arab-25th-anniversary',
    hreflang: {
      ja: '/news/jumeirah-burj-al-arab-25th-anniversary',
      en: '/en/news/jumeirah-burj-al-arab-25th-anniversary',
      default: '/news/jumeirah-burj-al-arab-25th-anniversary'
    }
  },
  '/en/news/global-butler-shortage-salary-surge': {
    title: 'Global Butler Shortage Causes Salary Surge, Industry Changes | News | Japan Butler Association',
    description: 'Analysis of the current situation where salaries are surging due to global butler shortage. Detailed explanation of challenges in securing talent in the butler industry and future prospects.',
    keywords: 'butler shortage, salary surge, talent acquisition, industry trends, global',
    canonical: '/en/news/global-butler-shortage-salary-surge',
    hreflang: {
      ja: '/news/global-butler-shortage-salary-surge',
      en: '/en/news/global-butler-shortage-salary-surge',
      default: '/news/global-butler-shortage-salary-surge'
    }
  },
  '/en/news/weave-robotics-isaac-robot-butler': {
    title: 'Weave Robotics Announces AI Butler Robot "Isaac" | News | Japan Butler Association',
    description: 'Weave Robotics announces AI butler robot "Isaac". Detailed explanation of the possibilities of butler robots combining the latest robotics technology and AI, and their impact on the butler industry.',
    keywords: 'Weave Robotics, Isaac, AI butler robot, robotics, technology',
    canonical: '/en/news/weave-robotics-isaac-robot-butler',
    hreflang: {
      ja: '/news/weave-robotics-isaac-robot-butler',
      en: '/en/news/weave-robotics-isaac-robot-butler',
      default: '/news/weave-robotics-isaac-robot-butler'
    }
  }
};
